import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import RegisterStepsNew from "../../components/UI/RegisterStepsNew/RegisterStepsNew.tsx";
import styles from "./ResumenNew.module.scss";
import { motion } from "framer-motion";
import { fireAnalytics } from "../../utils/firebase.js";
import { ArrowLeft, ArrowRight, CheckCircle, User, Building, MapPin, Phone, Globe, FileText, Mail } from "lucide-react";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { PersonFormData, CompanyFormData } from "../../types/register.ts";
import LoaderAnimation from "../../components/UI/LoaderAnimation/LoaderAnimation.tsx";
import { completeOnboardingUpdate } from "../../store/actions/profileActions";
import { getRegistrationEmail } from "../../store/actions/registerActions";
import defaultPersonImage from "../../assets/images/userDefaultHelmet.png";

interface ResumenNewProps {
  type: string;
  personaForm: PersonFormData;
  empresaForm: CompanyFormData;
  userSkills: any[];
  userData: any;
  setLoginAnimation: (value: boolean) => void;
  loginAnimation: boolean;
  completeOnboardingUpdate: (navigate: any, image?: File, email?: string) => void;
}

const variants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: { 
    opacity: 1,
    y: 0,
    scale: 1
  }
};

const ResumenNew: React.FC<ResumenNewProps> = ({ type, personaForm, empresaForm, userData, setLoginAnimation, loginAnimation, completeOnboardingUpdate }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [workZonesNew, setWorkZonesNew] = useState<any[]>([]);
  const [allCountryNew, setAllCountryNew] = useState<boolean>(false);
  const [personaFormNew, setPersonaFormNew] = useState<any>(null);
  const [empresaFormNew, setEmpresaFormNew] = useState<any>(null);
  const [userSkillsNew, setUserSkillsNew] = useState<any[]>([]);
  const [accountTypeNew, setAccountTypeNew] = useState<string>("");
  const [userImage, setUserImage] = useState<File | null>(null);
  const [userImageBase64, setUserImageBase64] = useState<string | null>(null);
  const [defaultImageForBackend, setDefaultImageForBackend] = useState<File | null>(null);
  const [registrationEmail, setRegistrationEmail] = useState<string>("");
  const navigate = useNavigate();
  
  useEffect(() => {
    // Mantener evento antiguo durante transición
    fireAnalytics("ob_step_3", { category: "navigation" });
    
    // Nuevo evento estandarizado
    fireAnalytics("onboarding_step_3_view", {
      category: "onboarding",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      action: "step_view",
      step: 3,
      account_type: (accountTypeNew || type) || ""
    });
    
    const storedAccountType = localStorage.getItem("accountTypeNew");
    
    const storedPersonaForm = localStorage.getItem("personaFormNew");
    const storedEmpresaForm = localStorage.getItem("empresaFormNew");
    const storedUserSkills = localStorage.getItem("userSkillsNew");
    
    const storedWorkZones = localStorage.getItem("workZonesNew");
    const storedAllCountry = localStorage.getItem("allCountryNew");
    
    const storedImage = localStorage.getItem("userImageNew");
    
    const email = getRegistrationEmail();
    if (email) {
      setRegistrationEmail(email);
    }
    
    if (storedAccountType) {
      setAccountTypeNew(JSON.parse(storedAccountType));
    }
    
    if (storedPersonaForm) {
      setPersonaFormNew(JSON.parse(storedPersonaForm));
    }
    
    if (storedEmpresaForm) {
      setEmpresaFormNew(JSON.parse(storedEmpresaForm));
    }
    
    if (storedUserSkills) {
      setUserSkillsNew(JSON.parse(storedUserSkills));
    }
    
    if (storedWorkZones) {
      setWorkZonesNew(JSON.parse(storedWorkZones));
    }
    
    if (storedAllCountry) {
      setAllCountryNew(JSON.parse(storedAllCountry));
    }

    if (storedImage) {
      try {
        setUserImageBase64(storedImage);
        const fileFromBase64 = dataURLtoFile(storedImage, "userImage.jpg");
        setUserImage(fileFromBase64);
      } catch (error) {
        console.error("Error al cargar imagen del localStorage:", error);
      }
    }

    const accountType = JSON.parse(storedAccountType || 'null') || type;
    if (accountType === "PERSON") {
      fetch(defaultPersonImage)
        .then(res => res.blob())
        .then(blob => {
          const defaultFile = new File([blob], "defaultPersonImage.jpg", { type: "image/jpeg" });
          setDefaultImageForBackend(defaultFile);
        })
        .catch(error => {
          console.error("Error al cargar imagen por defecto para persona:", error);
        });
    } else if (accountType === "COMPANY") {

      fetch(defaultPersonImage)
        .then(res => res.blob())
        .then(blob => {
          const defaultFile = new File([blob], "defaultCompanyImage.jpg", { type: "image/jpeg" });
          setDefaultImageForBackend(defaultFile);
        })
        .catch(error => {
          console.error("Error al cargar imagen por defecto para empresa:", error);
        });
    }
    
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const actualYear = new Date().getFullYear();

  const handleConfirm = () => {
    // Mantener evento antiguo durante transición
    fireAnalytics("ob_step_3_confirm", { category: "onboarding" });
    
    // Nuevo evento estandarizado
    fireAnalytics("onboarding_step_3_confirm", {
      category: "onboarding",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      action: "step_confirm",
      step: 3,
      account_type: (accountTypeNew || type) || ""
    });
    
    let imageToSend = userImage;
    if (!imageToSend && defaultImageForBackend) {
      imageToSend = defaultImageForBackend;
    }
    
    completeOnboardingUpdate(navigate, imageToSend, registrationEmail);
  };

  const handleBack = () => {
    navigate("/work-zones-new");
  };


  return (
    <motion.div
      className={styles.container}
      initial="hidden"
      animate={isLoaded ? "visible" : "hidden"}
      variants={variants}
      transition={{ 
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.1
      }}
    >
      <section className={styles.resumen}>
        <motion.div 
          className={styles.resumen_body}
          variants={variants}
        >
          <RegisterStepsNew stepCount={4} />
          
          <motion.h1 
            className={styles.title}
            variants={variants}
          >
            Resumen de tu cuenta
          </motion.h1>
          
          <motion.p 
            className={styles.subtitle}
            variants={variants}
          >
            Revisa que todos los datos sean correctos antes de continuar
          </motion.p>

          <motion.div 
            className={styles.content}
            variants={variants}
          >
            {/* Sección principal integrada */}
            <div className={styles.integratedSection}>
              {/* Imagen y datos principales */}
              <div className={styles.mainHeader}>
                <div className={styles.imageSection}>
                  <div className={styles.imageContainer}>
                    {userImageBase64 ? (
                      <img 
                        src={userImageBase64} 
                        alt="Foto de perfil" 
                        className={styles.profileImage}
                      />
                    ) : (
                      <div className={styles.defaultImage}>
                        {(accountTypeNew || type) === "PERSON" ? (
                          <img 
                            src={defaultPersonImage} 
                            alt="Imagen por defecto" 
                            className={styles.defaultProfileImage}
                          />
                        ) : (
                          <Building size={40} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={styles.mainInfo}>
                  <div className={styles.accountType}>
                    <span className={styles.accountTypeLabel}>
                      {(accountTypeNew || type) === "PERSON" ? "Profesional" : "Empresa"}
                    </span>
                  </div>
                  
                  <div className={styles.contactInfo}>
                    <div className={styles.contactGrid}>
                      {(accountTypeNew || type) === "PERSON" ? (
                        <div className={styles.contactItem}>
                          <User size={16} />
                          <span>{personaFormNew?.name || userData?.name || personaForm.name || "No especificado"}</span>
                          <span>{personaFormNew?.lastName || userData?.last_name || personaForm.lastName || "No especificado"}</span>
                        </div>
                      ) : (
                        <div className={styles.contactItem}>
                          <Building size={16} />
                          <span>{empresaFormNew?.companyName || userData?.razon_social || empresaForm.companyName || "No especificado"}</span>
                        </div>
                      )}
                      
                      <div className={styles.contactItem}>
                        <Mail size={16} />
                        <span>{registrationEmail || "email@ejemplo.com"}</span>
                      </div>
                      
                      <div className={styles.contactItem}>
                        <Phone size={16} />
                        <span>{(accountTypeNew || type) === "PERSON" ? (personaFormNew?.tel || "No especificado") : (empresaFormNew?.tel || "No especificado")}</span>
                      </div>
                      
                      <div className={styles.contactItem}>
                        <Globe size={16} />
                        <span>{(accountTypeNew || type) === "PERSON" ? (personaFormNew?.webSite || "No especificado") : (empresaFormNew?.webSite || "No especificado")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills integrados */}
              <div className={styles.skillsSection}>
                <div className={styles.skillsHeader}>
                  <FileText size={18} />
                  <span>Skills y Rubros</span>
                </div>
                <div className={styles.skillsContainer}>
                  {userSkillsNew.length > 0 ? (
                    userSkillsNew.map((skill, index) => (
                      <span key={index} className={styles.skillTag}>
                        {skill.name}
                      </span>
                    ))
                  ) : (
                    <span className={styles.noSkills}>No se han seleccionado skills</span>
                  )}
                </div>
              </div>

              {/* Zonas de trabajo integradas */}
              <div className={styles.workZonesSection}>
                <div className={styles.workZonesHeader}>
                  <MapPin size={18} />
                  <span>Zonas de Trabajo</span>
                </div>
                <div className={styles.workZonesContainer}>
                  {allCountryNew ? (
                    <span className={styles.allCountryTag}>
                      <CheckCircle size={16} />
                      Todo el país
                    </span>
                  ) : workZonesNew && workZonesNew.length > 0 ? (
                    workZonesNew.map((zone, index) => (
                      <span key={index} className={styles.zoneTag}>
                        {zone.jurisdiccion?.name || zone.jurisdiccion} - {zone.city}
                      </span>
                    ))
                  ) : (
                    <span className={styles.noZones}>No se han seleccionado zonas</span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className={styles.actions}
            variants={variants}
          >
            <button
              onClick={handleBack}
              className={`${styles.customButton} ${styles.hover}`}
            >
              <ArrowLeft size={18} />
              <span>Volver</span>
            </button>
            
            <button
              className={`${styles.customButton} ${styles.primaryButton}`}
              onClick={handleConfirm}
            >
              <span>Confirmar y Continuar</span>
              <ArrowRight size={18} />
            </button>
          </motion.div>

          <motion.div 
            className={styles.copyright}
            variants={variants}
          >
            Copyright © {actualYear} Edify, All rights reserved. Buenos Aires, Argentina.
          </motion.div>
        </motion.div>
        <LoaderAnimation isActive={loginAnimation} />
      </section>
    </motion.div>
  );
};

const mapStateToProps = (state) => {
  return {
    type: state.register.type,
    personaForm: state.register.personaForm,
    empresaForm: state.register.empresaForm,
    userSkills: state.register.userSkills || [],
    userData: state.general.userData,
    loginAnimation: state.general.loginAnimation,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setLoginAnimation: (value) => dispatch({ type: "SET_LOGIN_ANIMATION", payload: value }),
    completeOnboardingUpdate: (navigate: any, image?: File, email?: string) => dispatch(completeOnboardingUpdate(navigate, image, email)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResumenNew);
